import { Box, Drawer, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import NavRail from "./navRail";
import TopBar from "./topBar";
import ContextBar, { type ContextBarProps } from "./contextBar";
// LEGACY — delete these two imports and the isLegacy branches to remove it.
import LegacyBar from "./legacy/legacyBar";
import LegacyContextBar from "./legacy/legacyContextBar";
import { legacyMainSx, legacyPlainMainSx } from "./legacy/legacyMain";
import type { LegacyScreenChrome } from "./legacy/legacyScreens";
import { tokens } from "../../../theme";
import type { ShellChromeProps } from "./shellChrome";

export interface AppShellProps
  extends ContextBarProps,
    Omit<ShellChromeProps, "variant"> {
  /** Dark chrome signals "you have left a client context". */
  variant?: "client" | "system";
  /** Withhold the context bar — the legacy selection screen had no title block. */
  hideBar?: boolean;
  /** Legacy's tinted content field belonged to the dashboard views only; the
   *  selection screen sat on the plain page background. */
  plainMain?: boolean;
  /** LEGACY — per-screen chrome switches. */
  legacyChrome?: LegacyScreenChrome;
  children: React.ReactNode;
}

/**
 * The only shell in the app.
 *
 * Which chrome mounts is read from the active theme (`tokens.shell`), and both
 * take the same ShellChromeProps — so flipping sidebar <-> topbar is a config
 * change and nothing below this file moves. The rail sits beside the content,
 * the top bar above it; that axis is the only structural difference.
 *
 * Below `md` the rail becomes a temporary Drawer opened from the context bar,
 * because a permanent 240px rail leaves 135px of content on a 375px screen.
 *
 * Replaces the chrome previously spread across header.tsx, analytics.tsx,
 * trainingView.tsx, equipments.tsx and systemManagement.tsx.
 */
const AppShell = (props: AppShellProps) => {
  const {
    nav,
    selectedItemId,
    onSelectItem,
    variant = "client",
    onExitSystem,
    contextLabel,
    user,
    onSelectProgram,
    onOpenSystemManagement,
    onLogout,
    collapsed,
    onToggleCollapsed,
    hideBar,
    plainMain,
    legacyChrome,
    children,
    ...contextBarProps
  } = props;

  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [navOpen, setNavOpen] = React.useState(false);
  // LEGACY — the context bar is pinned and only <main> scrolls, so the legacy
  // bar needs to know when content has moved underneath it. The other four
  // themes already mark that seam themselves and do not use this.
  //
  // Boolean, not the offset: re-rendering the shell on every scroll frame would
  // be a real cost for a divider with exactly two states. The listener is only
  // attached under legacy, so the other themes pay nothing at all.
  const [scrolled, setScrolled] = React.useState(false);
  const onMainScroll = React.useCallback((e: React.UIEvent<HTMLElement>) => {
    const past = e.currentTarget.scrollTop > 0;
    setScrolled((was) => (was === past ? was : past));
  }, []);

  const isLegacy = tokens.shell === "legacy";
  const isRail = tokens.shell === "rail";
  const Chrome = isLegacy ? LegacyBar : isRail ? NavRail : TopBar;
  const Bar = isLegacy ? LegacyContextBar : ContextBar;
  // A drawer is dismissed by picking something, and collapsing it makes no
  // sense — it is already an overlay.
  const drawerRail = isRail && isCompact;

  const chrome = (
    <Chrome
      nav={nav}
      selectedItemId={selectedItemId}
      onSelectItem={(id) => {
        onSelectItem(id);
        if (drawerRail) setNavOpen(false);
      }}
      variant={variant}
      onExitSystem={onExitSystem}
      contextLabel={contextLabel}
      user={user}
      onSelectProgram={onSelectProgram}
      onOpenSystemManagement={onOpenSystemManagement}
      onLogout={onLogout}
      collapsed={drawerRail ? false : collapsed}
      onToggleCollapsed={drawerRail ? undefined : onToggleCollapsed}
      onOpenSwitcher={contextBarProps.onOpenSwitcher}
    />
  );

  // Fixed-height frame: the chrome and context bar never scroll, only <main>
  // does. minWidth/minHeight 0 on the content column is what lets it shrink
  // instead of being forced open by wide tables.
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isRail ? "row" : "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: tokens.color.canvas
      }}
    >
      {drawerRail ? (
        <Drawer
          variant="temporary"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { border: 0, bgcolor: "transparent" } }}
        >
          {chrome}
        </Drawer>
      ) : (
        chrome
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        {!hideBar && (
          <Bar
            {...contextBarProps}
            {...(isLegacy ? { chrome: legacyChrome, scrolled } : {})}
            onOpenNav={drawerRail ? () => setNavOpen(true) : undefined}
          />
        )}
        <Box
          component="main"
          onScroll={isLegacy ? onMainScroll : undefined}
          sx={
            // LEGACY — never falls through to the refresh's padding. A legacy
            // screen brings its own from its own JSX, so anything added here
            // stacks on top of it: `plainMain` used to land the grades table
            // 24px right of the original while its title stayed put.
            isLegacy
              ? plainMain
                ? // mainPadTop is the one exception to "add nothing": a screen
                  // with no tab strip has nothing holding its content off the
                  // bar. Still opt-in per screen, never a blanket default.
                  legacyChrome?.mainPadTop
                  ? { ...legacyPlainMainSx, pt: `${legacyChrome.mainPadTop}px` }
                  : legacyPlainMainSx
                : legacyMainSx
              : {
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "auto",
                  p: { xs: "12px 12px 20px", md: "20px 24px 28px" },
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  // Same rule as the legacy scrollFrame: a screen never
                  // shrinks below its content, or loading placeholders squash
                  // to slivers and the cards jump open when data lands.
                  "& > *": { flexShrink: 0 }
                }
          }
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
